
import macro from '@kitware/vtk.js/macros'
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer'
import Constants from '@kitware/vtk.js/Interaction/Widgets/OrientationMarkerWidget/Constants'

const { vtkErrorMacro } = macro
const { Corners } = Constants

// ----------------------------------------------------------------------------
// brandingWidget This is a hack from vtkOrientationWidget
// ----------------------------------------------------------------------------

function brandingWidget(publicAPI, model) {
  // Set our className
  model.classHierarchy.push('brandingWidget')

  const superClass = { ...publicAPI }

  const selfRenderer = vtkRenderer.newInstance()
  const resizeObserver = new ResizeObserver((entries) => {
    publicAPI.updateViewport()
  })
  let onAnimationSub = null
  let onEndAnimationSub = null
  let selfSubscription = null

  publicAPI.computeViewport = () => {
    selfRenderer.resetCamera()

    const parentRen =
      model.parentRenderer || model._interactor.getCurrentRenderer()

    const [xMin, yMin, xMax, yMax] = parentRen.getViewport()

    const view = model._interactor.getView()
    const canvasSize = view.getSize()
    const [viewXSize, viewYSize] = view.getViewportSize(parentRen)
    const minViewSize = Math.min(viewXSize, viewYSize)

    let pixelSize = model.viewportSize * minViewSize
    // clamp pixel size
    pixelSize = Math.max(
      Math.min(model.minPixelSize, minViewSize),
      Math.min(model.maxPixelSize, pixelSize)
    )

    const xFrac = pixelSize / canvasSize[0]
    const yFrac = pixelSize / canvasSize[1]
    
    const paddingX = pixelSize / canvasSize[0] * model.padding.x
    const paddingY = pixelSize / canvasSize[1] * model.padding.y

    // [left bottom right top]
    switch (model.viewportCorner) {
      case Corners.TOP_LEFT:
        return [xMin + paddingX, yMax - yFrac + paddingY, xMin + xFrac + paddingX, yMax + paddingY]
      case Corners.TOP_RIGHT:
        return [xMax - xFrac, yMax - yFrac, xMax, yMax]
      case Corners.BOTTOM_LEFT:
        return [xMin, yMin, xMin + xFrac, yMin + yFrac]
      case Corners.BOTTOM_RIGHT:
        return [xMax - xFrac - paddingX, yMin - paddingY, xMax - paddingX, yMin + yFrac - paddingY]
      default:
        vtkErrorMacro('Invalid widget corner')
        return null
    }
  }

  publicAPI.updateViewport = () => {
    if (model.enabled) {
      // @ts-ignore
      selfRenderer.setViewport(...publicAPI.computeViewport())
      model._interactor.render()
    }
  }

  /**
   * Enables/Disables the orientation marker.
   */
  publicAPI.setEnabled = (enabling) => {
    if (enabling) {
      if (model.enabled) {
        return
      }

      if (!model.actor) {
        vtkErrorMacro('Must set actor before enabling orientation marker.')
        return
      }

      if (!model._interactor) {
        vtkErrorMacro(
          'Must set interactor before enabling orientation marker.'
        )
        return
      }

      const ren =
        model.parentRenderer || model._interactor.getCurrentRenderer()
      const renderWindow = ren.getRenderWindow()
      renderWindow.addRenderer(selfRenderer)
      if (renderWindow.getNumberOfLayers() < 2) {
        renderWindow.setNumberOfLayers(2)
      }
      // Highest number is foreground
      selfRenderer.setLayer(renderWindow.getNumberOfLayers() - 1)
      selfRenderer.setInteractive(false)

      selfRenderer.addViewProp(model.actor)
      model.actor.setVisibility(true)

      resizeObserver.observe(model._interactor.getView().getCanvas())

      publicAPI.updateViewport()

      model.enabled = true
    } else {
      if (!model.enabled) {
        return
      }
      model.enabled = false

      resizeObserver.disconnect()

      model.actor.setVisibility(false)
      selfRenderer.removeViewProp(model.actor)

      const renderWindow = model._interactor
        .findPokedRenderer()
        .getRenderWindow()
      if (renderWindow) {
        renderWindow.removeRenderer(selfRenderer)
      }
    }
    publicAPI.modified()
  }

  /**
   * Sets the viewport corner.
   */
  publicAPI.setViewportCorner = (corner) => {
    if (corner === model.viewportCorner) {
      return
    }

    model.viewportCorner = corner
    publicAPI.updateViewport()
  }

  /**
   * Sets the viewport size.
   */
  publicAPI.setViewportSize = (sizeFactor) => {
    const viewportSize = Math.min(1, Math.max(0, sizeFactor))
    if (viewportSize === model.viewportSize) {
      return
    }

    model.viewportSize = viewportSize
    publicAPI.updateViewport()
  }

  publicAPI.setActor = (actor) => {
    const previousState = model.enabled
    publicAPI.setEnabled(false)
    model.actor = actor
    publicAPI.setEnabled(previousState)
  }

  publicAPI.setParentRenderer = (ren) => {
    const changed = superClass.setParentRenderer(ren)
    if (changed) {
      publicAPI.updateViewport()
    }
    return changed
  }

  publicAPI.getRenderer = () => selfRenderer

  publicAPI.delete = () => {
    superClass.delete()
    if (selfSubscription) {
      selfSubscription.unsubscribe()
      selfSubscription = null
    }
    if (onAnimationSub) {
      onAnimationSub.unsubscribe()
      onAnimationSub = null
    }
    if (onEndAnimationSub) {
      onEndAnimationSub.unsubscribe()
      onEndAnimationSub = null
    }
    resizeObserver.disconnect()
  }

  // --------------------------------------------------------------------------

  // update viewport whenever we are updated
  selfSubscription = publicAPI.onModified(publicAPI.updateViewport)
}

// ----------------------------------------------------------------------------
// Object factory
// ----------------------------------------------------------------------------

export const DEFAULT_VALUES = {
  // actor: null,
  // _interactor: null,
  viewportCorner: Constants.Corners.BOTTOM_LEFT,
  viewportSize: 0.2,
  minPixelSize: 50,
  maxPixelSize: 200,
  parentRenderer: null,
  padding: {x: .1, y: .3}
  // x: 0.04,
  // y: -0.01,
}

// ----------------------------------------------------------------------------

export function extend(publicAPI, model, initialValues = {}) {
  // FIX: Ensure model is an object if it wasn't passed in
  model = model || {}

  Object.assign(model, DEFAULT_VALUES, initialValues)

  // Build VTK API
  macro.obj(publicAPI, model)

  macro.get(publicAPI, model, ['enabled', 'viewportCorner', 'viewportSize'])

  // NOTE: setting these while the widget is enabled will
  // not update the widget.
  macro.setGet(publicAPI, model, [
    '_interactor',
    'minPixelSize',
    'maxPixelSize',
    'parentRenderer',
    'padding'
  ])
  macro.get(publicAPI, model, ['actor'])
  macro.moveToProtected(publicAPI, model, ['interactor'])

  // Object methods
  brandingWidget(publicAPI, model)
}

// ----------------------------------------------------------------------------

export const newInstance = macro.newInstance(
  extend,
  'brandingWidget'
)

// ----------------------------------------------------------------------------

export default { newInstance, extend, ...Constants }